import { errorLogger } from "@/Common/helpers/logger";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import {
	IChargebackTypes,
	IDeductionChargebackType,
	IGrossChargebackType,
	IMappedPayItems,
	IPayItems,
} from "@/Settings/types/Common/model";
import { IDepartmentChargebackCodes } from "@/Settings/types/SystemSettings/appendingConfiguration";
import { Box, Grid, Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import InfoSubTitle from "../../../common/InfoSubTitle";
import Title from "../../../common/Title";
import GrossChargebackSelector, { EMPTY_GROSS_CHARGEBACK_ITEM } from "./GrossChargebackSelector";
import DeductionsChargebackSelector, { EMPTY_DEDUCTION_CHARGEBACK_ITEM } from "./DeductionsChargebackSelector";
import { compact, isEqual, pick, sortBy } from "lodash-es";

const validateGrossChargebacks = (inputData: IGrossChargebackType[]) => {
	if (inputData.length === 0) return true;
	return inputData.every((item) => !!item.chargebackCode && !!item.productType);
};
const validateDeductionsChargebacks = (inputData: IDeductionChargebackType[]) => {
	if (inputData.length === 0) return true;
	return inputData.every((item) => !!item.chargebackCode && !!item.deductionPayItem);
};
const areArraysEqualByFields = <T extends object>(a: T[], b: T[], fields: (keyof T)[]): boolean => {
	if (a.length !== b.length) return false;

	const normalize = (arr: T[]) =>
		sortBy(
			arr.map((item) => pick(item, fields)),
			fields as string[]
		);

	return isEqual(normalize(a), normalize(b));
};

const resolvePayItemsOptions = (mappedPayItems: IMappedPayItems[]): IPayItems[] => {
	return compact(
		mappedPayItems.map((item) => {
			if (item.effectOnPay === "REDUCTION") {
				return {
					...item,
					group: "Deductions",
				};
			} else if (item.effectOnPay === "ADDITION") {
				return {
					...item,
					group: "Earnings",
				};
			}
		}) as IPayItems[]
	).sort((a, b) => {
		if ((a.group || "") > (b.group || "")) return -1;
		if ((a.group || "") < (b.group || "")) return 1;
		return 0;
	});
};

const ChargeBackTypes = (props: {
	locationId: string;
	position: string;
	isDepartmental: boolean;
	mappedPayItems?: IMappedPayItems[];
	chargebackTypes?: IChargebackTypes;
	positionChargebacksCodes?: IDepartmentChargebackCodes[];
}) => {
	const { locationId, position, chargebackTypes, positionChargebacksCodes = [], mappedPayItems = [] } = props;
	const grossChargebackTypes = chargebackTypes?.grossChargebackTypes ?? [];
	const deductionsChargebackTypes = chargebackTypes?.deductionsChargebackTypes ?? [];
	const payItemsOptions = resolvePayItemsOptions(mappedPayItems);

	const [selectedGrossChargebacks, setSelectedGrossChargebacks] = useState<IGrossChargebackType[]>([]);
	const [selectedDeductionsChargebacks, setSelectedDeductionsChargebacks] = useState<IDeductionChargebackType[]>([]);

	const selectableOptionsForGrossChargebacks = positionChargebacksCodes.filter((item) =>
		selectedDeductionsChargebacks.every((selected) => String(selected.chargebackCode) !== item.code)
	);

	const selectableOptionsForDeductionsChargebacks = positionChargebacksCodes.filter((item) =>
		selectedGrossChargebacks.every((selected) => String(selected.chargebackCode) !== item.code)
	);

	const { mutate, isPending } = useMutation({
		mutationFn: (input: {
			chargebackTypes: {
				position: string;
				grossChargebackTypes: IGrossChargebackType[];
				deductionsChargebackTypes: IDeductionChargebackType[];
			};
		}) => updateSystemSettings(locationId, input, "CHARGEBACK_TYPES"),
		onError: (error) => errorLogger(error),
	});

	const handleChangeGrossChargeback = (item: IGrossChargebackType, index: number) => {
		const updatedItems = [...selectedGrossChargebacks];
		updatedItems[index] = item;
		setSelectedGrossChargebacks(updatedItems);
		updateGrossChargebacks(updatedItems);
	};

	const handleDeleteGrossChargeback = (index: number) => {
		const updatedItems = [...selectedGrossChargebacks];
		updatedItems.splice(index, 1);
		setSelectedGrossChargebacks(updatedItems);
		updateGrossChargebacks(updatedItems);
	};

	const updateGrossChargebacks = (updatedItems: IGrossChargebackType[]) => {
		if (!updatedItems.length) {
			return mutate({
				chargebackTypes: {
					position,
					grossChargebackTypes: updatedItems,
					deductionsChargebackTypes: selectedDeductionsChargebacks,
				},
			});
		}
		const isValid = validateGrossChargebacks(updatedItems);
		const hasChanges = !areArraysEqualByFields(updatedItems, grossChargebackTypes, ["chargebackCode", "productType"]);
		if (isValid && hasChanges) {
			return mutate({
				chargebackTypes: {
					position,
					grossChargebackTypes: updatedItems,
					deductionsChargebackTypes: selectedDeductionsChargebacks,
				},
			});
		}
	};

	const handleChangeDeductionsChargeback = (item: IDeductionChargebackType, index: number) => {
		const updatedItems = [...selectedDeductionsChargebacks];
		updatedItems[index] = item;
		setSelectedDeductionsChargebacks(updatedItems);
		updateDeductionsChargebacks(updatedItems);
	};

	const handleDeleteDeductionsChargeback = (index: number) => {
		const updatedItems = [...selectedDeductionsChargebacks];
		updatedItems.splice(index, 1);
		setSelectedDeductionsChargebacks(updatedItems);
		updateDeductionsChargebacks(updatedItems);
	};

	const updateDeductionsChargebacks = (updatedItems: IDeductionChargebackType[]) => {
		if (!updatedItems.length) {
			return mutate({
				chargebackTypes: {
					position,
					grossChargebackTypes: selectedGrossChargebacks,
					deductionsChargebackTypes: updatedItems,
				},
			});
		}
		const isValid = validateDeductionsChargebacks(updatedItems);
		const hasChanges = !areArraysEqualByFields(updatedItems, deductionsChargebackTypes, [
			"chargebackCode",
			"earningPayItem",
			"deductionPayItem",
		]);
		if (isValid && hasChanges) {
			return mutate({
				chargebackTypes: {
					position,
					grossChargebackTypes: selectedGrossChargebacks,
					deductionsChargebackTypes: updatedItems,
				},
			});
		}
	};

	useEffect(() => {
		setSelectedGrossChargebacks(grossChargebackTypes);
		setSelectedDeductionsChargebacks(deductionsChargebackTypes);
		return () => {
			setSelectedGrossChargebacks([]);
			setSelectedDeductionsChargebacks([]);
		};
	}, [chargebackTypes]);

	return (
		<Box>
			<Title title="Chargeback Types" className="!mb-[24px]" />
			<Grid container direction={"row"} mt="15px">
				<Grid item xs={12}>
					<Stack direction="row" alignItems="center" gap={16} className="!mb-[12px]">
						<InfoSubTitle title="Cost Of Sale" />
						<InfoSubTitle title="Product Type" />
					</Stack>
					<Grid item className="position-mapping-payroll-column" sx={{ position: "relative" }}>
						<GrossChargebackSelector
							items={selectedGrossChargebacks}
							selectableOptions={selectableOptionsForGrossChargebacks}
							isPending={isPending}
							position={position}
							onAdd={() => setSelectedGrossChargebacks([...selectedGrossChargebacks, EMPTY_GROSS_CHARGEBACK_ITEM])}
							onChange={handleChangeGrossChargeback}
							onDelete={handleDeleteGrossChargeback}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12} mt={2}>
					<Stack direction="row" alignItems="center" gap={20.35} className="!mb-[12px]">
						<InfoSubTitle title="Net Pay" />
						<InfoSubTitle title="Pay Item(s)" />
					</Stack>
					<DeductionsChargebackSelector
						items={selectedDeductionsChargebacks}
						selectableOptions={selectableOptionsForDeductionsChargebacks}
						payItemsOptions={payItemsOptions}
						isPending={isPending}
						position={position}
						onAdd={() => setSelectedDeductionsChargebacks([...selectedDeductionsChargebacks, EMPTY_DEDUCTION_CHARGEBACK_ITEM])}
						onChange={handleChangeDeductionsChargeback}
						onDelete={handleDeleteDeductionsChargeback}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};

export default ChargeBackTypes;
